import React, { useState } from 'react';
import {
  Box, Typography, Button, Divider, Tooltip, CircularProgress, Dialog, TextField, IconButton, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  CardGiftcard, Event, Password, Undo, Block, Layers, AdminPanelSettings, HourglassTop, Verified, Close, LocalOffer, Sms,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useGetGiftVoucherSaleQuery, useRequestGiftVoucherApprovalMutation } from '../../../services/giftVoucherApi';
import { setGvApprovalRequest, setGvServerSale, setGvDiscount, selectGvSale } from '../../../store/giftVoucherSaleSlice';
import { useVoucherApproval } from '../../../hooks/useVoucherApproval';
import { useToast } from '../../common/ToastProvider';
import { GV, inr, inr2, localTotals, validityLabel, pinLabel, APPROVAL_LABEL } from './gvUtils';

const capSx = { fontSize: '0.58rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.09rem', textTransform: 'uppercase' };

function Pill({ icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '20px', px: 1.1, py: 0.4, fontSize: '0.66rem', fontWeight: 700, color: '#334155' }}>
      {React.cloneElement(icon, { sx: { fontSize: 13, color: '#94A3B8' } })}{children}
    </Box>
  );
}

// ── One approval requirement (discount / quantity) ──────────────────────────
function ApprovalBanner({ kind, compact }) {
  const dispatch = useDispatch();
  const showToast = useToast();
  const sale = useSelector((s) => s.giftVoucherSale);
  const req = sale.approvalRequests[kind];
  const [requestApproval, { isLoading: isRequesting }] = useRequestGiftVoucherApprovalMutation();
  const { refetch } = useGetGiftVoucherSaleQuery(sale.saleId, { skip: !sale.saleId || !req });
  const [otp, setOtp] = useState('');
  const approval = useVoucherApproval(req?.status === 'pending' ? req.id : null, {
    onApproved: async () => {
      dispatch(setGvApprovalRequest({ kind, id: req.id, status: 'approved' }));
      showToast('Approved by manager', 'success');
      try {
        const res = await refetch().unwrap();
        dispatch(setGvServerSale({ sale: res?.data?.gift_voucher_sale, approvalsRequired: res?.data?.approvals_required }));
      } catch { /* the next draft save refreshes it */ }
    },
    onRejected: (r) => {
      dispatch(setGvApprovalRequest({ kind, id: req.id, status: r.status }));
      showToast(r.status === 'rejected' ? 'Manager rejected the request' : `Approval ${r.status}`, 'error');
    },
  });

  const label = kind === 'voucher_sale_discount' && sale.discount
    ? `${sale.discount.type === 'percentage' ? `${Number(sale.discount.value)}%` : inr(sale.discount.value)} discount`
    : APPROVAL_LABEL[kind];

  const request = async () => {
    try {
      const res = await requestApproval({ id: sale.saleId, kind, reason: sale.discount?.reason || 'Voucher sale' }).unwrap();
      const r = res?.data?.discount_approval_request;
      dispatch(setGvApprovalRequest({ kind, id: r.id, status: 'pending' }));
      showToast('Approval requested', 'info');
    } catch (err) {
      showToast(err?.data?.error || 'Could not request approval', 'error');
    }
  };

  const base = { display: 'flex', gap: 1, alignItems: 'flex-start', px: 1.4, py: compact ? 0.75 : 1.1, borderRadius: '8px', fontSize: '0.72rem', lineHeight: 1.4 };
  if (req?.status === 'approved') {
    return (
      <Box sx={{ ...base, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }}>
        <Verified sx={{ fontSize: 17 }} />
        <Box sx={{ flex: 1 }}><b>Approved</b> · {label}{!compact && <Box sx={{ fontSize: '0.66rem', mt: 0.25 }}>By {approval.approvedBy || 'manager'}. Editing lines or the discount needs a new approval.</Box>}</Box>
      </Box>
    );
  }
  if (req?.status === 'pending') {
    return (
      <Box sx={{ ...base, flexDirection: 'column', bgcolor: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'flex-start' }}>
          <HourglassTop sx={{ fontSize: 17 }} />
          <Box sx={{ flex: 1 }}><b>Waiting for manager</b> · {label}{!compact && <Box sx={{ fontSize: '0.66rem', mt: 0.25 }}>Approve on the manager’s screen or enter their OTP.</Box>}</Box>
          <CircularProgress size={14} sx={{ color: '#94A3B8', mt: 0.25 }} />
        </Box>
        {!compact && (approval.cycle ? (
          <Box sx={{ display: 'flex', gap: 0.75, width: '100%', mt: 0.5 }}>
            <TextField size="small" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit OTP"
              inputProps={{ inputMode: 'numeric', 'aria-label': 'Manager OTP' }} sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: 30, bgcolor: '#FFF', borderRadius: '6px', fontSize: '0.78rem' } }} />
            <Button size="small" disabled={otp.length !== 6 || approval.isVerifying} onClick={() => approval.verifyOtpCode(otp)}
              sx={{ minWidth: 0, height: 30, px: 1.25, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.68rem', bgcolor: '#4F46E5', color: '#FFF', '&:hover': { bgcolor: '#4338CA' }, '&.Mui-disabled': { bgcolor: '#E2E8F0' } }}>
              Verify
            </Button>
            <Button size="small" onClick={approval.resendOtpCycle} disabled={approval.isResending}
              sx={{ minWidth: 0, height: 30, px: 1, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.68rem', color: '#64748B', border: '1px solid #E2E8F0', bgcolor: '#FFF' }}>
              Resend
            </Button>
          </Box>
        ) : (
          <Button size="small" startIcon={<Sms sx={{ fontSize: '14px !important' }} />} onClick={approval.startOtpCycle} disabled={approval.isStarting}
            sx={{ alignSelf: 'flex-start', ml: 3, height: 26, px: 1, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.66rem', color: '#4F46E5', bgcolor: '#FFF', border: '1px solid #C7D2FE' }}>
            Use manager OTP
          </Button>
        ))}
      </Box>
    );
  }
  return (
    <Box sx={{ ...base, bgcolor: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E', alignItems: 'center' }}>
      <AdminPanelSettings sx={{ fontSize: 17 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}><b>{compact ? 'Approval needed' : 'Manager approval needed'}</b> · {label}</Box>
      <Button size="small" onClick={request} disabled={isRequesting || !sale.saleId || sale.dirty}
        sx={{ minWidth: 0, height: 28, px: 1.25, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.68rem', color: '#64748B', border: '1px solid #E2E8F0', bgcolor: '#FFF', flexShrink: 0 }}>
        {isRequesting ? <CircularProgress size={12} /> : compact ? 'Request' : 'Request approval'}
      </Button>
    </Box>
  );
}

export function GiftVoucherApprovals({ compact }) {
  const approvals = useSelector((s) => s.giftVoucherSale.approvalsRequired);
  const requests = useSelector((s) => s.giftVoucherSale.approvalRequests);
  // Keep showing an approved banner after the server stops listing that kind.
  const kinds = [...new Set([...approvals, ...Object.keys(requests).filter((k) => requests[k]?.status === 'approved')])];
  if (!kinds.length) return null;
  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>{kinds.map((k) => <ApprovalBanner key={k} kind={k} compact={compact} />)}</Box>;
}

// ── Center card: rules of the focused line's type + approvals ───────────────
export function GiftVoucherTypeCard({ types }) {
  const sale = useSelector((s) => s.giftVoucherSale);
  const line = sale.lines.find((l) => l.key === sale.focusLineKey) || sale.lines[0];
  const type = line ? types.find((t) => t.id === line.typeId) : null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1, overflowY: 'auto' }}>
      {type ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Typography sx={capSx}>Voucher type</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: GV.bg, border: `1px solid ${GV.border}`, color: GV.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CardGiftcard sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: '0.95rem', fontWeight: 900, color: '#0F172A' }}>{type.name}</Typography>
              <Typography noWrap sx={{ fontSize: '0.68rem', color: '#64748B', fontWeight: 600 }}>
                {type.denominations.map((d) => inr(d)).join(' · ')}{type.allow_custom_amount ? ` · or ${type.min_amount ? inr(type.min_amount) : '₹1'}–${type.max_amount ? inr(type.max_amount) : 'any'}` : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Pill icon={<Event />}>{validityLabel(type)}</Pill>
            <Pill icon={<Password />}>{pinLabel(type)}</Pill>
            <Pill icon={type.refundable ? <Undo /> : <Block />}>{type.refundable ? 'Refundable' : 'Non-refundable'}</Pill>
            {type.max_quantity_per_sale && <Pill icon={<Layers />}>Max {type.max_quantity_per_sale} per sale</Pill>}
          </Box>
          {type.terms && (
            <Typography sx={{ fontSize: '0.68rem', color: '#64748B', lineHeight: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', px: 1.25, py: 1 }}>{type.terms}</Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
          <CardGiftcard sx={{ color: '#E2E8F0', fontSize: 32, mb: 1 }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#CBD5E1', textAlign: 'center' }}>Add a voucher line to see the type’s rules</Typography>
        </Box>
      )}
      <GiftVoucherApprovals />
    </Box>
  );
}

// ── Summary card (replaces BILLING DETAILS) ─────────────────────────────────
export function GiftVoucherSummaryCard() {
  const sale = useSelector((s) => s.giftVoucherSale);
  const t = localTotals(sale);
  const payable = sale.server && !sale.dirty ? Number(sale.server.amount_payable) : t.payable;
  const discount = sale.server && !sale.dirty ? Number(sale.server.discount_total) : t.disc;
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: GV.main, letterSpacing: '0.05rem' }}>VOUCHER SALE</Typography>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#CBD5E1' }}>{t.count} voucher{t.count !== 1 ? 's' : ''}</Typography>
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
          <Typography sx={{ fontSize: '0.68rem' }}>Face value</Typography>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 600 }}>{inr2(t.face)}</Typography>
        </Box>
        {discount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#10B981' }}>Discount{sale.discount?.type === 'percentage' ? ` ${Number(sale.discount.value)}%` : ''}</Typography>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#10B981' }}>−{inr2(discount)}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
          <Typography sx={{ fontSize: '0.68rem' }}>Tax</Typography>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#94A3B8' }}>Not applicable</Typography>
        </Box>
        {sale.syncError && <Typography sx={{ fontSize: '0.64rem', color: '#DC2626', mt: 0.5 }}>{sale.syncError}</Typography>}
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.75rem' }}>PAYABLE</Typography>
          <Typography sx={{ fontWeight: 900, color: '#4F46E5', fontSize: { md: '1.2rem', lg: '1.4rem' } }}>{inr2(payable)}</Typography>
        </Box>
      </Box>
    </>
  );
}

// Why Collect is disabled (null when ready).
export function useCollectBlock(types, sessionBlocked) {
  const sale = useSelector(selectGvSale);
  if (sessionBlocked) return 'Open a cash session to continue';
  if (!sale.buyer) return 'Select the buyer first (F2)';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(sale.buyerEmail.trim())) return 'Enter a valid email for delivery';
  if (!sale.lines.length) return 'Add at least one voucher';
  if (sale.delivery === 'individual' && sale.lines.some((l) => l.recipients.length !== Number(l.qty) || l.recipients.some((r) => !r.email))) return 'Add a person for every voucher';
  if (sale.syncError) return sale.syncError;
  if (sale.dirty || !sale.server) return 'Saving…';
  if (sale.approvalsRequired.length) return 'Manager approval is needed first';
  if (!types.length) return 'No gift vouchers are on sale at this store';
  return null;
}

export function GiftVoucherButtons({ block, onDiscard, onCollect, height = 52 }) {
  const sale = useSelector((s) => s.giftVoucherSale);
  const t = localTotals(sale);
  const payable = sale.server && !sale.dirty ? Number(sale.server.amount_payable) : t.payable;
  return (
    <>
      <Button fullWidth variant="outlined" onClick={onDiscard} disabled={!sale.buyer && !sale.lines.length}
        sx={{ height, color: '#EF4444', borderColor: '#EF4444', fontWeight: 800, borderRadius: '8px', '&:hover': { bgcolor: '#FEF2F2', borderColor: '#EF4444' } }}>
        DISCARD
      </Button>
      <Tooltip title={block || ''} placement="top" arrow>
        <span>
          <Button fullWidth variant="contained" disableElevation disabled={!!block} onClick={onCollect}
            sx={{ height, bgcolor: '#4F46E5', fontWeight: 900, borderRadius: '8px', fontSize: '0.82rem', letterSpacing: '0.02rem', '&:hover': { bgcolor: '#4338CA' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}>
            {block === 'Saving…' ? <CircularProgress size={18} sx={{ color: '#94A3B8' }} /> : `COLLECT ${inr(payable)}`}
          </Button>
        </span>
      </Tooltip>
    </>
  );
}

// ── Discount dialog ─────────────────────────────────────────────────────────
function DiscountContent({ onClose, maxPercent }) {
  const dispatch = useDispatch();
  const showToast = useToast();
  const current = useSelector((s) => s.giftVoucherSale.discount);
  const [type, setType] = useState(current?.type || 'percentage');
  const [value, setValue] = useState(current?.value != null ? String(current.value) : '');
  const [reason, setReason] = useState(current?.reason || '');

  const apply = () => {
    const v = Number(value);
    if (!(v > 0) || !/^\d+(\.\d{1,2})?$/.test(value)) { showToast('Enter a discount above zero with at most 2 decimals', 'error'); return; }
    if (type === 'percentage' && maxPercent != null && v > maxPercent) { showToast(`This voucher type allows at most ${maxPercent}% discount`, 'error'); return; }
    if (!reason.trim()) { showToast('A reason is required', 'error'); return; }
    dispatch(setGvDiscount({ type, value: v, reason: reason.trim() }));
    showToast('Discount added', 'info');
    onClose();
  };

  return (
    <>
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#F0FDF4', color: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LocalOffer sx={{ fontSize: 20 }} /></Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#1E293B' }}>Discount on voucher sale</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>Above the cashier limit needs a manager’s approval</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }} aria-label="Close"><Close sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <ToggleButtonGroup exclusive fullWidth size="small" value={type} onChange={(e, v) => v && setType(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, fontSize: '0.8rem', '&.Mui-selected': { bgcolor: '#EEF2FF', color: '#4F46E5' } } }}>
          <ToggleButton value="percentage">Percent %</ToggleButton>
          <ToggleButton value="amount">Amount ₹</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" label={type === 'percentage' ? 'Discount (%)' : 'Discount (₹)'} value={value} autoFocus
          onChange={(e) => setValue(e.target.value.replace(/[^\d.]/g, ''))} inputProps={{ inputMode: 'decimal' }}
          helperText={type === 'percentage' && maxPercent != null ? `Up to ${maxPercent}% for these vouchers` : ' '}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
        <TextField size="small" label="Reason *" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Corporate order" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
      </Box>
      <Box sx={{ px: 2.5, pb: 2, display: 'flex', gap: 1 }}>
        {current && (
          <Button onClick={() => { dispatch(setGvDiscount(null)); showToast('Discount removed', 'info'); onClose(); }}
            sx={{ mr: 'auto', borderRadius: '8px', textTransform: 'none', fontWeight: 700, color: '#EF4444', border: '1px solid #FECACA' }}>
            Remove
          </Button>
        )}
        <Button onClick={onClose} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, border: '1px solid #E2E8F0', color: '#64748B' }}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={apply} sx={{ bgcolor: '#4F46E5', borderRadius: '8px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#4338CA' } }}>Apply</Button>
      </Box>
    </>
  );
}

export function VoucherDiscountDialog({ open, onClose, maxPercent }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', m: { xs: 2, sm: 3 } } }}>
      {open && <DiscountContent onClose={onClose} maxPercent={maxPercent} />}
    </Dialog>
  );
}

// ── Mobile total row (inside DashboardMobileControls) ───────────────────────
export function GiftVoucherMobileTotal({ block }) {
  const sale = useSelector((s) => s.giftVoucherSale);
  const t = localTotals(sale);
  const payable = sale.server && !sale.dirty ? Number(sale.server.amount_payable) : t.payable;
  return (
    <>
      <Box sx={{ px: 1.5, py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ minWidth: 0, mr: 1 }}>
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 900, color: GV.main, letterSpacing: '0.04rem' }}>PAYABLE · NOT A TAX INVOICE</Typography>
          <Typography noWrap sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 600 }}>
            {t.count} voucher{t.count !== 1 ? 's' : ''}{t.disc > 0 ? ` · −${inr(t.disc)} discount` : ''}
          </Typography>
        </Box>
        <Typography sx={{ fontWeight: 900, color: '#4F46E5', fontSize: '1.15rem', flexShrink: 0 }}>{inr2(payable)}</Typography>
      </Box>
      {block && block !== 'Saving…' && <Typography sx={{ px: 1.5, pb: 0.25, fontSize: '0.66rem', fontWeight: 700, color: '#B45309' }}>{block}</Typography>}
    </>
  );
}
